import { useApiMutation, useApiQuery } from "@gladis/api-client";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  EXPENSE_CATEGORIES,
  formatEur,
  formatPercent,
  type ExpenseCategoryValue,
} from "@gladis/shared";
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ErrorState,
  LoadingState,
  PersonLabel,
  Table,
  filterControlClassName,
} from "@gladis/ui";
import {
  expenseCategoryLabel,
  errorSentence,
  formatDayAndMonth,
  tRich,
  useT,
} from "@gladis/ui/i18n";

import { expenseScopeOf } from "./ExpenseFilters.js";
import { nextSort, sortRows, type ExpenseSort, type ExpenseSortColumn } from "./sort-rows.js";
import { groupMatchedByChild, isSearching, searchRows } from "./search-rows.js";
import { createContext, useContext, useState, type ReactNode } from "react";

import { intervalLabel } from "./intervals.js";

/** One key for the whole page, because the page is one request. */
export const EXPENSES_KEY = ["expenses"] as const;

/**
 * A group of rows, collapsed into one line with a monthly total.
 *
 * Declared here beside `ExpenseRowDto` rather than imported from `@gladis/shared`, exactly as its
 * sibling is: this component takes the shape it renders, so the SPA can pass a parsed DTO and a test
 * can pass a literal. `kind` is `"group"` and NOT `"vehicleGroup"` — this table does not know what a
 * vehicle is, and `sourceId` is opaque to it for the same reason `ExpenseRowDto.sourceId` already is.
 */
export interface ExpenseGroupRowDto {
  kind: "group";
  sourceId: string;
  label: string;
  childCount: number;
  interval: "monthly";
  amountInEuros: number;
  monthlyEquivalentInEuros: number;
  children: ExpenseRowDto[];
}

/** What the table renders: an entered expense, a mortgage line, or a group of either. */
export type ExpenseListRow = ExpenseRowDto | ExpenseGroupRowDto;

export interface ExpenseRowDto {
  /** `derived` is a mortgage part: read-only in its figures, attributable in who pays. */
  kind: "expense" | "derived";
  id: string;
  description: string;
  amountInEuros: number;
  interval: string;
  category: ExpenseCategoryValue;
  startDate: string;
  monthlyEquivalentInEuros: number;
  hasStarted: boolean;
  archivedAt: string | null;
  card: { id: string; name: string; identifier: string | null; isArchived: boolean } | null;
  splits: {
    memberId: string | null;
    memberDisplayName: string;
    percentage: number;
    isFrozen: boolean;
  }[];
  /** On a mortgage row: how much of the payment is interest. Null on an entered expense. */
  interestInEuros: number | null;
  /** On a mortgage row: the mortgage part, which its attribution is keyed on. */
  sourceId: string | null;
}

export interface CardSummaryDto {
  id: string;
  name: string;
  ownership: string;
  ownerId: string | null;
  ownerName: string | null;
  identifier: string | null;
  accessibleIdentifier: string | null;
  bankCode: string | null;
  lastFour: string | null;
  monthlyTotalInEuros: number;
  isJoint: boolean;
  archivedAt: string | null;
}

export interface ExpensesPageDto {
  asOf: string;
  settleUp: {
    debtorId: string;
    debtorName: string;
    creditorId: string;
    creditorName: string;
    amountInEuros: number;
  }[];
  rows: ExpenseListRow[];
  monthlyTotalInEuros: number;
  /**
   * The monthly total decomposed by who is responsible for it — the splits, not the cards.
   *
   * In identity's member order, and a share stored against somebody who has left is flagged rather than
   * dropped. See `memberShares` in the domain for why it is not the same figure as the settle-up.
   */
  byMember: { memberId: string; displayName: string; monthlyInEuros: number; isFrozen: boolean }[];
  byCategory: { category: ExpenseCategoryValue; totalInEuros: number }[];
  cards: CardSummaryDto[];
  /** True when the properties read failed, so the mortgage rows are missing and the total is short. */
  mortgageUnavailable: boolean;
}

export function useExpenses(filters?: {
  category?: string;
  state?: "active" | "archived";
  cardId?: string;
  member?: string;
}) {
  const query = new URLSearchParams();
  if (filters?.category) query.set("category", filters.category);
  if (filters?.state) query.set("state", filters.state);
  if (filters?.cardId) query.set("cardId", filters.cardId);
  if (filters?.member) query.set("member", filters.member);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";

  return useApiQuery<ExpensesPageDto>([...EXPENSES_KEY, suffix], `/expenses${suffix}`, {
    /*
     * ALWAYS REFETCHED ON MOUNT — the one page in the application where the global 30-second
     * `staleTime` is wrong.
     *
     * Every other page's payload is written only by that page, so a cached copy is stale by at most
     * one member's own action. This payload contains figures THIS FEATURE DOES NOT OWN: the mortgage
     * interest is computed from a mortgage part edited on the Properties page, and it changes on its
     * own every month besides. Nothing here can observe either.
     *
     * The symptom is specific and was found in the e2e journey, not reasoned about: add a mortgage
     * part, navigate to Expenses, and the derived section is ABSENT and the household total omits the
     * interest — for thirty seconds, with no error and nothing to click. The page's own numbers are
     * quietly smaller than the truth, which is the failure this feature guards hardest against.
     *
     * The alternative was cross-feature invalidation: the properties mutations invalidating the
     * expenses key. That means `@gladis/domain-properties` knowing an expenses cache key exists, which
     * is the coupling the two packages are forbidden from having — and it would still miss the
     * month-to-month change, which no mutation causes.
     */
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * The recurring expenses, with what the household is committed to per month.
 *
 * TWO THINGS THIS COMPONENT DOES NOT DO, DELIBERATELY.
 *
 * **It does not compute a total.** The monthly total and the category totals arrive from the server,
 * already summed from the same rounded figures the rows display. Adding the rows up here would be a second
 * implementation of the product's rounding rule, and the two would disagree by a cent or two on exactly the
 * households that have weekly expenses.
 *
 * **It does not decide whether an expense has started.** `hasStarted` arrives from the server, resolved once
 * for the whole response. A browser in another timezone deciding it locally gets it wrong on precisely the
 * day it matters — and here that is not a mislabelled row, it is a row the member believes is counting when
 * it is not.
 */
export function ExpenseList({
  filters,
  members = [],
  controls,
  onEdit,
  onAttribute,
  onSource,
  nextDue,
}: {
  filters?: { category?: string; state?: "active" | "archived"; cardId?: string; member?: string };
  /**
   * Each row's next due date, from the schedule read (feature 024). Passed in rather than read here, so this
   * list never issues a second request on its own — the page already holds the schedule for its section.
   */
  nextDue?: Readonly<Record<string, string | null>> | undefined;
  /**
   * Current active members, from identity — for the FACES in the "Whose" column and nothing else.
   *
   * NOT FOR THE NAMES. Every share carries its own snapshotted `memberDisplayName`, which is the only
   * correct source once somebody has left (009's FR-024 rule, and the reason `owner_display_name`
   * exists at all). This list resolves an `avatarUrl` and misses for a departed member, which is
   * precisely right: 020's FR-022 says such a row keeps its initials and loses its face.
   *
   * Optional, so a caller that has not wired it renders initials throughout — the degradation this
   * component should have, rather than a missing column.
   */
  members?: readonly { userId: string; displayName: string; avatarUrl?: string | null }[];
  /**
   * Opens the form on an existing expense.
   *
   * A CALLBACK RATHER THAN A FORM RENDERED HERE. `PATCH /api/expenses/:id` and `editExpense` shipped with
   * the feature and had no way to be reached — the list offered archive and restore and nothing else, so an
   * expense could be retired but never corrected. The route owns where a form appears, exactly as it owns
   * where the create form appears; the list's job is to say which row.
   */
  onEdit?: (row: ExpenseRowDto) => void;
  /** Opens the attribution editor on a mortgage row — which card pays it, and whose cost it is. */
  onAttribute?: (row: ExpenseRowDto) => void;
  /**
   * Opening whatever produced a group — a vehicle today, a property later.
   *
   * It takes an OPAQUE id and a label, so this table never learns what the thing is. FR-072's rule
   * applies: exactly one action, offered directly, because a one-item menu is a click spent on a
   * choice that does not exist.
   */
  onSource?: (sourceId: string, label: string) => void;
  /**
   * This section's own filter row, rendered under its heading.
   *
   * PASSED IN RATHER THAN BUILT HERE, for the reason the add button is not here either: the page owns
   * the filter STATE — it is what the route holds and what `useExpenses` is called with — and a list
   * that constructed its own controls would be reading a value it does not own from a component it
   * would then have to be handed anyway. What the list owns is WHERE they go, which is this slot.
   */
  controls?: ReactNode;
}): ReactNode {
  const t = useT();
  const { data, isPending, error } = useExpenses(filters);
  /*
   * THE SORT LIVES HERE, WHERE THE FILTERS DELIBERATELY DO NOT.
   *
   * The page owns the filter state because the trend and the four stat cards read it too — it decides
   * what every figure on the page is ABOUT. A sort decides nothing: it reorders one table's rows in
   * the browser and changes no total, so nothing outside this component could use it. Lifting it to
   * the route would put a value in the page's hands that only this table can read.
   *
   * `null` is the order the server sent. See `nextSort` for why that is a state and not an absence.
   */
  const [sort, setSort] = useState<ExpenseSort | null>(null);
  /*
   * WHICH GROUPS ARE OPEN, held here for the reason the sort is: a fold reorders nothing and changes
   * no figure, so nothing outside this component could read it. `sort-rows.ts` states the rule —
   * "a sort reorders one table and changes no total" — and a fold is the same class of control.
   */
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set());
  const toggleGroup = (sourceId: string) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  };
  /*
   * THE SEARCH TERM LIVES HERE FOR THE REASON THE SORT DOES, and NOT for the reason the archived
   * toggle does not.
   *
   * The toggle is passed in from the page because it goes on the WIRE — `state` is part of
   * `useExpenses`' query key, so the page owns it and the server answers it. A search touches no
   * request: it hides rows the browser is already holding, and nothing outside this table could read
   * it. Lifting it would put a value in the page's hands that only this component can use.
   */
  const [query, setQuery] = useState("");

  if (isPending) return <LoadingState>{t("exp.list.loading")}</LoadingState>;
  if (error)
    return <ErrorState>{t("exp.list.loadFailed", { reason: errorSentence(error) })}</ErrorState>;

  const showingArchived = filters?.state === "archived";

  /*
   * The mortgage rows, for the footnote below the table. Summed from the DISPLAYED figures, which is safe and
   * only safe here: there are at most a handful of parts, both figures are already rounded to the cent by the
   * server, and this sum is presentational — every total that matters is computed in integer cents there.
   */
  /*
   * THE ROWS AS DRAWN. Sorted in the browser over the whole payload — see `sort-rows.ts` for why there
   * is no `?sort=` on the wire, and why this is a copy rather than a sort in place.
   */
  const rows = sortRows(searchRows(data.rows, query), sort, (row) =>
    nextDue === undefined ? null : nextDueOfRow(row, nextDue),
  );

  /*
   * A GROUP FOUND THROUGH A CHILD OPENS ITSELF.
   *
   * Searching "insurance" while a car's costs are folded matches the group — `search-rows.ts` reads
   * the children so the search cannot claim the household has no insurance. But a row that matched
   * for a reason the reader cannot see is worse than no match at all, so the fold opens and the
   * matching cost is on screen.
   *
   * Derived rather than stored: it follows the query and needs no clearing when the box empties,
   * which a piece of state would.
   */
  const autoOpen = new Set(
    rows
      .filter((row): row is ExpenseGroupRowDto => isGroupRow(row))
      .filter((row) => groupMatchedByChild(row, query))
      .map((row) => row.sourceId),
  );
  const searching = isSearching(query);

  /** One column's state and toggle, in the shape `Table.HeaderCell` asks for. */
  const sortFor = (column: ExpenseSortColumn) => ({
    direction: sort?.column === column ? sort.direction : null,
    onToggle: () => {
      setSort((current) => nextSort(current, column));
    },
  });

  /*
   * NARROWED, because `rows` became a union in 023. A group is never a mortgage line — a derived row
   * has no `expenses` row for a link table to name — so this predicate is unchanged in meaning and
   * only had to say so to the compiler.
   */
  const mortgageRows = data.rows.filter((row): row is ExpenseRowDto => row.kind === "derived");
  const mortgageTotal = mortgageRows.reduce((sum, row) => sum + row.monthlyEquivalentInEuros, 0);
  const mortgageInterest = mortgageRows.reduce((sum, row) => sum + (row.interestInEuros ?? 0), 0);
  /*
   * WHOSE FIGURES THESE ARE, decided in one place for the sentence below AND the category strip at the
   * bottom of this section.
   *
   * The strip is why this is a variable rather than an inline test. It used to print the HOUSEHOLD's
   * per-category breakdown directly beneath one member's rows, under a heading naming nobody — the total
   * above it at least changed its sentence, so the strip was the one figure on the page that was silently
   * about somebody else. Both now come from `scope`.
   */
  const scope = expenseScopeOf(filters, members);

  /*
   * Whether the list below is a SUBSET of what the total describes.
   *
   * `member` is NOT among these any more. It scopes the total as well as the rows, so the two describe
   * the same set and share a sentence again — a different sentence, which names the member. What remains
   * here is the filters that narrow rows alone: the archived view (whose total is deliberately the ACTIVE
   * set) and the category and card filters the interface no longer offers but the wire still accepts.
   */
  const isFiltered =
    showingArchived ||
    searching ||
    filters?.category !== undefined ||
    filters?.cardId !== undefined;

  return (
    <section className="rounded-control border border-line bg-panel p-5">
      {/*
        THE SUBJECT ON THE LEFT, ITS CONTROLS ON THE RIGHT — the arrangement `Page.Title` uses for a
        page, applied to a section. They were on a line of their own below the heading, which spent a
        whole row on two controls and pushed the table down; on the title row they read as belonging
        to this table rather than to the page, which is the distinction that keeps them out of the
        page's own title row beside the whose filter.

        `items-start` rather than `items-baseline`: the left column is two or three lines of text and
        the right is a pair of 36px boxes, and a baseline shared between them would hang the controls
        off the heading's first line.
      */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">
            {showingArchived ? t("exp.list.archivedHeading") : t("exp.list.heading")}
          </h2>
          {/*
            FOUR PHRASINGS, BECAUSE THE FIGURE IS ABOUT A DIFFERENT SET IN EACH.

            Under a member scope the total is THAT MEMBER's share and the sentence names them. Under
            `joint` it is the rows nobody's name is on. With no scope but some other filter on — the
            archived view is the only one the interface offers — the total is still the household's whole
            ACTIVE commitment while the count is what is listed, so the two get two clauses: the first
            draft said "€ 1.400,00 per month across 1 expense" in the archived view, which invites the
            reader to believe the €1.400,00 is that one archived row. Unfiltered, one sentence.
          */}
          <p className="mt-1 text-sm text-muted">
            {tRich(
              scope.kind === "member"
                ? "exp.list.shownMember"
                : scope.kind === "joint"
                  ? "exp.list.shownJoint"
                  : isFiltered
                    ? "exp.list.shownFiltered"
                    : "exp.list.shownAll",
              {
                amount: (
                  <span className="font-semibold text-ink">
                    {formatEur(data.monthlyTotalInEuros)}
                  </span>
                ),
              },
              {
                /*
                 * THE ROWS AS DRAWN, not the payload's.
                 *
                 * This read `data.rows.length` until the search landed, and it was right for as long as
                 * every row in the payload was on screen. It is the count clause of a sentence whose whole
                 * job is to say how many rows are listed beneath it — "5 expenses shown" above two of them
                 * is the same defect this page was reported for, in the one place that exists to prevent it.
                 */
                count: rows.length,
                // Only read by `shownMember`; harmless in the other three, which have no `{name}`.
                ...(scope.kind === "member" ? { name: scope.name } : {}),
              },
            )}
          </p>
          {/*
            THE SEARCH, ON A LINE OF ITS OWN rather than folded into the sentence above.

            The sentence above already carries two clauses — what is listed, and what the figure is
            about — and threading the term through it would need one catalogue entry per scope, four
            of them, to say one thing. This states the narrowing once, beside the count it explains,
            and leaves the figure's own sentence alone in every scope.
          */}
          {searching ? (
            <p className="mt-1 text-xs text-muted">
              {t("exp.list.matching", { query: query.trim() })}
            </p>
          ) : null}
        </div>

        {/*
          THE SEARCH BOX AND THE TABLE'S OPTIONS MENU.

          Both act on THIS TABLE, which is what earns them a place in this header rather than in the
          page's title row — the scope filter is up there because it governs the trend and the four
          cards as well, and a control beside it would claim to do the same.

          The label is `sr-only`: a placeholder is not an accessible name, and a search box beside a
          table needs no visible caption to be understood.

          `shrink-0` so the pair keeps its width when the heading's sentence is long; below `sm` the
          header wraps and this becomes a full row of its own, which is where `w-full` on the input
          earns its place.
        */}
        <div className="flex shrink-0 items-center gap-2">
          <label className="sr-only" htmlFor="expense-search">
            {t("exp.list.searchLabel")}
          </label>
          {/*
          A BARE `<input>` WEARING THE FILTER CHROME, and deliberately not `TextField`.
          
          `TextField` is the FORM field — `border-input` over a `bg-input/30` fill — and that is the
          right look for something collecting a value and the wrong one for a control that says what
          you are looking at. Reported as exactly that: the search did not match the whose filter
          beside it. Passing the chrome to `TextField` through `className` was the other option and is
          the trap `filterControlClassName` documents: `cn` is a plain join, so both fills would land
          in the class list and the winner would change between builds.
          
          `type="search"` for the platform's own clear affordance and the keyboard it asks for.
        */}
          <input
            id="expense-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder={t("exp.list.searchPlaceholder")}
            data-testid="expense-search"
            className={`${filterControlClassName} w-full border-line bg-transparent text-ink placeholder:text-muted sm:max-w-64`}
          />
          {controls}
        </div>
        {/*
          NO ADD BUTTON HERE. Adding an expense is the PAGE's primary action and lives at the end of the
          title row, where `Page.Title` puts every page's — the same move the Cards page made out of
          `CardSet`, for the same reason: a control in this header sits at whatever height this section
          happens to start, which is below a chart and a settle-up panel once those are on the page.

          THE FILTERS ARE A DIFFERENT CASE AND DO BELONG HERE. An add button acts on the household; a
          filter acts on THIS TABLE, and it used to sit in a row above the chart where it looked like it
          acted on the page. Its height moving with the section is the point rather than the objection.
        */}
      </header>

      {rows.length === 0 ? (
        <div className="mt-5">
          {/*
            THREE EMPTY STATES, because "there is nothing" and "nothing matched what you typed" send
            a reader to different places. A household that has entered no expenses needs the
            invitation; one whose search found nothing needs to know the box is why, and that
            clearing it brings the rows back.
          */}
          <EmptyState>
            {searching
              ? t("exp.list.searchEmpty", { query: query.trim() })
              : showingArchived
                ? t("exp.list.archivedEmpty")
                : t("exp.list.empty")}
          </EmptyState>
        </div>
      ) : (
        <ExpenseTable
          rows={rows}
          members={members}
          onEdit={onEdit}
          onAttribute={onAttribute}
          onSource={onSource}
          sortFor={sortFor}
          openGroups={new Set([...openGroups, ...autoOpen])}
          onToggleGroup={toggleGroup}
          nextDue={nextDue}
        />
      )}

      {/*
        THE INTEREST BREAKDOWN, ONCE UNDER THE TABLE rather than per row.
        
        A mortgage row shows the PAYMENT, because that is what leaves the account and what every total counts.
        How much of it was interest and how much bought equity does not fit in a cell and does not belong in
        one — it is the same sentence for every mortgage row and a reader needs it once. Losing it entirely was
        the risk in moving these into the table: it is the figure that reconciles this page with a mortgage
        statement.
      */}
      {mortgageRows.length > 0 ? (
        <p className="mt-4 text-sm text-muted">
          {tRich(
            "exp.list.mortgageSplit",
            {
              amount: <span className="font-semibold text-ink">{formatEur(mortgageTotal)}</span>,
            },
            {
              count: mortgageRows.length,
              interest: formatEur(mortgageInterest),
              principal: formatEur(mortgageTotal - mortgageInterest),
            },
          )}
        </p>
      ) : null}

      {/*
        AND THE ONE THING A MISSING ROW CANNOT SAY FOR ITSELF. If the properties read failed there is no
        mortgage row, which is indistinguishable from a household that has no mortgage — except that the total
        above is short by it. Money absent with no error is the failure this feature guards hardest against.
      */}
      {data.mortgageUnavailable ? (
        <p className="mt-4 text-sm text-danger">{t("exp.list.mortgageUnavailable")}</p>
      ) : null}

      {data.byCategory.length > 0 ? (
        <div className="mt-5 border-t border-line pt-4">
          {/*
            THE HEADING CARRIES THE SCOPE, because this strip has no sentence of its own and sits directly
            under the rows — so an unqualified "Per category, monthly" is read as the breakdown of them.
            That reading was wrong for as long as the filter existed.

            AND IT NAMES THE HOUSEHOLD WHEN THE ROWS ABOVE ARE A SUBSET. The member scope was fixed on
            2026-08-20 and this half was left behind: `byCategory` is computed over the active set
            whatever `state` says, so the ARCHIVED view has always listed archived rows above the
            ACTIVE household's categories, under a heading naming nothing. Search would have been the
            third control to land in that same gap. A subset with no scope named reads as the
            breakdown of what is on screen, every time.
          */}
          <h3 className="text-sm font-semibold text-muted">
            {scope.kind === "member"
              ? t("exp.chart.perCategoryMember", { name: scope.name })
              : scope.kind === "joint"
                ? t("exp.chart.perCategoryJoint")
                : isFiltered
                  ? t("exp.chart.perCategoryHousehold")
                  : t("exp.chart.perCategory")}
          </h3>
          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {data.byCategory.map((entry) => (
              <li key={entry.category} className="tabular-nums">
                <span className="text-muted">{expenseCategoryLabel(entry.category)}</span>{" "}
                {formatEur(entry.totalInEuros)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Whose cost this row is — the split, printed on the row it belongs to.
 *
 * ==========================================================================================
 * IT IS THE SPLIT AND NOT THE CARD, WHICH IS THE DISTINCTION THE COLUMN EXISTS TO MAKE
 * ==========================================================================================
 *
 * "Paid from" beside it says where the money came from. This says whose cost it was, and the two are
 * routinely different: a card owned by one member can pay a cost that member holds none of. That gap,
 * summed over the page, IS the settle-up — so a reader who wanted to know why a transfer is €260 can
 * now find it on the rows instead of taking the panel's word for it.
 *
 * ==========================================================================================
 * PERCENTAGES, NOT EUROS
 * ==========================================================================================
 *
 * The percentage is what is STORED; the euro share is arithmetic over it. Printing euros here would put
 * a fourth implementation of "40% of €433,33" on the page — the settle-up and the `byMember` card already
 * share exactly one, in the domain, precisely so they cannot drift a cent apart on screen — and this cell
 * has no access to it. FR-064's rule covers the reader instead: the row shows its monthly equivalent two
 * columns along and its percentage here, and the multiplication is theirs to do and reproduce.
 *
 * ==========================================================================================
 * NO SPLIT IS "JOINT", AND THAT IS A FACT RATHER THAN A BLANK
 * ==========================================================================================
 *
 * An empty split means household-shared with nobody named against it, which is a legitimate and common
 * state — not missing data. It is written out for the same reason `AccountList` writes "Joint" rather than
 * leaving the owner cell empty: an em dash reads as "not filled in yet".
 *
 * A FROZEN SHARE KEEPS ITS NAME AND ITS PERCENTAGE and is marked in TEXT, never by colour alone. It is a
 * real share, it is in the household's total, and somebody is paying it.
 */
function WhoseCell({
  splits,
  members,
}: {
  splits: ExpenseRowDto["splits"];
  members: readonly { userId: string; displayName: string; avatarUrl?: string | null }[];
}): ReactNode {
  const t = useT();

  /*
   * "Joint" GETS EVERY MEMBER'S FACE, which is what `PersonLabel`'s `group` is for.
   *
   * The first version drew an avatar-sized SPACER and the word beside it, on the argument that the
   * empty box keeps "Joint" on the same left edge as the names above it. Measured, that was true —
   * the text starts at the identical x — and it looked wrong anyway, which is the part that matters:
   * every other row opens with a circle, so the eye reads the column's left edge as where the circles
   * are, and "Joint" sat 32px inside it with dead space in front. Reported as "not lining up", and it
   * was: the alignment the spacer preserves is not the alignment a reader perceives.
   *
   * `group` is the better answer than deleting the spacer, and it is 020's own: a household-shared
   * cost is not about NOBODY — it is about EVERYBODY, and "Daily update" and "A former member" are
   * the rows that genuinely have no face. So the mark is the members' faces, the column has an
   * unbroken left edge, and the row says something true that a blank could not.
   *
   * `isPerson={false}` is what stops "Joint" being rendered as a name: initials from it would be "J",
   * for a person who does not exist, and the label is printed whole rather than shortened to its
   * first word.
   */
  if (splits.length === 0) {
    return (
      <PersonLabel
        label={t("person.joint")}
        isPerson={false}
        group={members.map((member) => ({
          name: member.displayName,
          avatarUrl: member.avatarUrl ?? null,
        }))}
      />
    );
  }

  return (
    /*
     * ONE MEMBER PER ROW, ALWAYS — never wrapped inline with a separator.
     *
     * Two shares side by side line up with nothing, so a reader comparing "who pays what" down the
     * table is re-finding each name on every row. Stacked, the faces form a column and the pills form
     * a column, and the comparison is a glance. It costs row height on a two-way split and that is the
     * trade: this table has few rows and the question it is most often opened for is this one.
     */
    <ul className="flex flex-col gap-1">
      {splits.map((split) => (
        /*
         * Keyed on the member id, falling back to the snapshotted name.
         *
         * `memberId` is nullable on the DTO — a share whose member row is gone keeps only the name it was
         * recorded under — and two such shares on one expense cannot exist, because a split holds one row
         * per member. The index would be the other option and it is worse: it re-keys every row below when
         * one share is removed.
         */
        <li key={split.memberId ?? split.memberDisplayName} className="flex items-center gap-2">
          {/*
            THE FACE IS LOOKED UP BY ID; THE NAME NEVER IS. A departed member misses the lookup and
            keeps their initials from the stored name, which is 020's FR-022 exactly: the row still
            knows who it was about, so it draws initials rather than the blank a nameless record gets.

            `PersonLabel` rather than an `Avatar` and a `<span>` assembled here. It already draws the
            face, prints the FIRST name beside it, keeps the full one on `title` for a screen reader
            and anything matching on text, and — with `isPerson` — decides the one rule that is easy
            to get wrong. Assembling it by hand meant a second copy of `firstNameOf`, which the design
            system exports, for a shortening rule stated in that component's own documentation.
          */}
          <PersonLabel
            label={split.memberDisplayName}
            isPerson
            avatarUrl={members.find((member) => member.userId === split.memberId)?.avatarUrl}
            // A frozen share still names a real person and keeps their initials; only the TEXT
            // recedes, which is what says "no longer a member" without relying on the words alone.
            {...(split.isFrozen ? { className: "text-muted" } : {})}
          />
          {/*
            A PILL, so the percentage reads as a label on the person rather than as a second figure in
            a row that already has one. It takes the table's own border token and no fill — a filled
            chip here would compete with the amount, which is the number the column beside it exists
            to show.
          */}
          <span className="rounded-full border border-line px-1.5 py-0.5 text-xs tabular-nums text-muted">
            {formatPercent(split.percentage)}
          </span>
          {split.isFrozen ? (
            <span className="text-xs text-muted">({t("exp.split.former")})</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * An entered expense's actions, behind one control — edit, and archive or put back.
 *
 * ONE TRIGGER RATHER THAN TWO BUTTONS, which is what every other table in the application does
 * (income's sources, a property's mortgage parts, a stock). Two text buttons in the last cell made
 * this column as wide as "Edit" plus "Restore" and put the row's controls in competition with its
 * figures — and this table's figures are the reason anyone opens the page.
 *
 * ITS OWN COMPONENT SO THE MUTATION HOOK IS PER-ROW rather than one hook driving whichever row was
 * clicked last — which is the bug that shape produces: two rapid clicks and the second row's request
 * carries the first row's pending state.
 *
 * The labels say what will happen, not what the row is. "Archive this expense" and "Restore this
 * expense" are actions; an item reading "Archived" is a status nobody can tell is selectable.
 */
function RowActions({
  row,
  onEdit,
  extra,
}: {
  row: ExpenseRowDto;
  onEdit: ((row: ExpenseRowDto) => void) | undefined;
  /** Appended after Edit and Archive — the vehicle page's "Detach from this vehicle". */
  extra: readonly ActionMenuItem[];
}): ReactNode {
  const t = useT();
  const verb = row.archivedAt === null ? "archive" : "unarchive";

  const run = useApiMutation<void, void>(
    (client) => client.post<void>(`/expenses/${row.id}/${verb}`, {}),
    { invalidates: [EXPENSES_KEY] },
  );

  return (
    <ActionMenu
      // Named after the row, because seven triggers all reading "Actions" is seven identical
      // accessible names in one table.
      label={`Actions for ${row.description}`}
      items={[
        ...(onEdit === undefined
          ? []
          : [
              {
                label: t("exp.list.edit"),
                onSelect: () => {
                  onEdit(row);
                },
              },
            ]),
        {
          label: verb === "archive" ? t("exp.list.archive") : t("exp.list.restore"),
          // Archiving retires a cost from every total; restoring puts one back. Only the first is
          // the set's one removing action.
          destructive: verb === "archive",
          disabled: run.isPending,
          onSelect: () => {
            run.mutate();
          },
        },
        ...extra,
      ]}
    />
  );
}

/** The category options a filter or a picker offers. Derived, never a literal list. */
export const CATEGORY_OPTIONS = EXPENSE_CATEGORIES;

/**
 * One ordinary row — an entered expense or a derived mortgage line.
 *
 * EXTRACTED IN 023 AND OTHERWISE UNCHANGED. It is rendered twice now: at the top level, and indented
 * inside an open group. Keeping it one component is what stops the two drifting — a child that
 * rendered its card or its splits differently from a top-level row would be the same expense
 * described two ways on one page.
 */
function LeafRow({
  row,
  members,
  onEdit,
  onAttribute,
  extraActions,
  nested = false,
}: {
  row: ExpenseRowDto;
  members: readonly MemberRef[];
  onEdit?: ((row: ExpenseRowDto) => void) | undefined;
  onAttribute?: ((row: ExpenseRowDto) => void) | undefined;
  extraActions?: ((row: ExpenseRowDto) => ActionMenuItem[]) | undefined;
  nested?: boolean;
}) {
  const t = useT();
  return (
    <Table.Row key={row.id}>
      <Table.Cell>
        {/*
          INDENTED WHEN NESTED, so a child reads as belonging to the row above it. Padding rather
          than a tree glyph: the group's own disclosure button already carries `aria-expanded`, and a
          second decorative marker would say the structure twice.

          THE INDENT IS ON THE WHOLE BLOCK AND NOT ON THE NAME, which is the fix for a real
          misalignment. It used to sit on the description's own `<span>`, so the category line
          underneath — and the "Starts …" badge's wrap — stayed flush with the un-indented column
          while the title moved right. A child row read as two competing left edges, and the deeper
          the indent the worse it looked.
        */}
        <div className={nested ? "ml-6" : undefined}>
          <span className="font-medium">{row.description}</span>
          {/*
                    THE BADGE IS WHAT MAKES ONE TABLE WORKABLE. A mortgage row's figure is computed from an
                    amortisation schedule and cannot be edited or archived here, and a row that looks exactly
                    like an editable one is a row a member will try to edit. It says what it is in TEXT, so it
                    survives greyscale and a screen reader.
                  */}
          {row.kind === "derived" ? (
            <span className="ml-2 rounded-full border border-line px-2 py-0.5 text-xs text-muted">
              {t("exp.list.mortgage")}
            </span>
          ) : null}
          {/* Not yet started is stated in TEXT, not by greying the row out. The row's own figure is
                      the full amount; what it contributes to the total is nothing, and a member needs to be
                      able to tell those apart. */}
          {row.hasStarted ? null : (
            <span className="ml-2 rounded-full border border-line px-2 py-0.5 text-xs text-muted">
              Starts {row.startDate}
            </span>
          )}
          {/*
                    THE CATEGORY, UNDER THE NAME RATHER THAN IN A COLUMN OF ITS OWN.

                    It is a property of the expense — "Rent, which is Housing" — not an independent fact
                    sitting at the same rank as the amount, and a column of seventeen possible words was
                    spending a sixth of the table's width to say something the description usually implies.

                    STILL TEXT, and still the catalogue's label rather than the raw value, so it is
                    searchable, translatable and readable to a screen reader in the row it belongs to. A
                    colour chip would have been the other way to shrink it, and it would have encoded the
                    category in hue alone — which `tokens.css` refuses for exactly this reason.
                  */}
          <div className="text-xs text-muted">{expenseCategoryLabel(row.category)}</div>
        </div>
      </Table.Cell>
      <Table.Cell>
        {row.card === null ? (
          <span className="text-muted">&mdash;</span>
        ) : (
          <>
            {row.card.name}
            {/* An archived card keeps showing on the expenses bound to it (FR-043, FR-044). */}
            {row.card.isArchived ? (
              <span className="ml-1 text-xs text-muted">(archived)</span>
            ) : null}
          </>
        )}
      </Table.Cell>
      <Table.Cell>
        <WhoseCell splits={row.splits} members={members} />
      </Table.Cell>
      <Table.Cell>{intervalLabel(row.interval)}</Table.Cell>
      <NextDueCell row={row} />
      <Table.Cell numeric>
        {formatEur(row.amountInEuros)}
        {/*
                    INSIDE THE FOLD, AND ONLY THERE, THE MONTHLY EQUIVALENT COMES BACK (FR-035).

                    This table dropped its monthly-equivalent column deliberately, and the comment on
                    the header records what that cost: it "is what made every settle-up line
                    reproducible by hand from the numbers printed on the row (FR-064)… It is the
                    non-monthly rows that lose it."

                    A fold is where the loss actually bites. Two €300.00 quarterly costs display as
                    €300.00 and €300.00 beneath a parent reading €200.00 — nothing is wrong, and
                    nothing on screen says so. So the figure returns here, for grouped rows only,
                    without reversing the decision for the table.

                    Suppressed when it would be the same number twice: a monthly row's raw amount IS
                    its monthly equivalent, and printing it again reads as an error.
                  */}
        {nested && row.interval !== "monthly" ? (
          <div className="text-xs text-muted">
            {t("exp.list.perMonth", {
              amount: formatEur(row.monthlyEquivalentInEuros),
            })}
          </div>
        ) : null}
      </Table.Cell>
      <Table.Cell numeric>
        <div className="flex items-center justify-end gap-3">
          {/*
                      A MORTGAGE ROW OFFERS "Who pays" AND NOTHING ELSE (FR-072).
                      
                      Not a disabled Edit and not a disabled Archive: a greyed control invites a member to work
                      out why it will not respond, where an absent one and a present one that does something
                      useful together say where the figure lives. What IS editable is the attribution, which is
                      this feature's to own — the amount belongs to the mortgage part, on the property.
                    */}
          {row.kind === "derived" ? (
            onAttribute === undefined ? null : (
              <button
                type="button"
                onClick={() => {
                  onAttribute(row);
                }}
                className="rounded-control px-1 text-sm text-muted underline decoration-dotted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <span aria-hidden="true">{t("exp.list.whoPays")}</span>
                <span className="sr-only">Set who pays {row.description}</span>
              </button>
            )
          ) : (
            <RowActions row={row} onEdit={onEdit} extra={extraActions?.(row) ?? []} />
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

/** The member shape the row cells read. Structural, like every other prop in this file. */
interface MemberRef {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
}

/** Narrows the union without every caller repeating the discriminant. */
/**
 * Each row's next due date, from the schedule read (feature 024, FR-028) — a context rather than a prop on
 * every row, because it reaches leaf rows inside folds two components down. `null` means the table has no
 * such column (the vehicle detail page), which is different from a row whose date is unknown.
 */
const NextDueContext = createContext<Readonly<Record<string, string | null>> | null>(null);

/** The key the schedule read uses for a row: the expense id, or `mortgage:<sourceId>`. */
export function scheduleKeyOf(row: ExpenseRowDto): string {
  return row.kind === "derived" && row.sourceId !== null ? `mortgage:${row.sourceId}` : row.id;
}

/** A row's next due date; a group's is its earliest child's, so it sorts where its first payment falls. */
export function nextDueOfRow(
  row: ExpenseListRow,
  nextDue: Readonly<Record<string, string | null>>,
): string | null {
  if (!isGroupRow(row)) return nextDue[scheduleKeyOf(row)] ?? null;
  const dates = row.children
    .map((child) => nextDue[scheduleKeyOf(child)] ?? null)
    .filter((date): date is string => date !== null)
    .sort();
  return dates[0] ?? null;
}

function NextDueCell({ row }: { row: ExpenseListRow }): ReactNode {
  const t = useT();
  const nextDue = useContext(NextDueContext);
  if (nextDue === null) return null;
  const date = nextDueOfRow(row, nextDue);
  return (
    <Table.Cell>
      {date === null ? (
        <span className="text-muted" aria-label={t("exp.list.noDueDate")}>
          &mdash;
        </span>
      ) : (
        formatDayAndMonth(date)
      )}
    </Table.Cell>
  );
}

function isGroupRow(row: ExpenseListRow): row is ExpenseGroupRowDto {
  return "kind" in row && row.kind === "group";
}

/**
 * A group and, when open, the rows it holds.
 *
 * ==========================================================================================
 * FOUR CELLS ANSWERED, TWO LEFT EMPTY — AND THE TWO ARE NOT A GAP
 * ==========================================================================================
 *
 * "Paid from" and "Whose" are precisely the two columns this table already declines to SORT, for the
 * identical stated reason: they hold sets rather than values. A car's costs sit on different cards
 * and split differently, so a group cannot fill them for the same reason it could never be ordered
 * by them. The fold carries the detail.
 *
 * ==========================================================================================
 * INSIDE THE FOLD, AND ONLY THERE, EACH CHILD SHOWS ITS MONTHLY EQUIVALENT
 * ==========================================================================================
 *
 * The table dropped its monthly-equivalent column deliberately, so the children display RAW amounts
 * at their own cadences — €300.00 quarterly beside €42.50 monthly — beneath a parent reading
 * €147.50. Nothing is wrong and nothing on screen says so. This is where the removed figure comes
 * back: only where it is needed, and without reversing the decision for the table.
 *
 * ONE ACTION, OFFERED DIRECTLY. FR-072's rule for a mortgage row, one kind along: a one-item menu is
 * a click spent on a choice that does not exist.
 */
function GroupRows({
  group,
  open,
  onToggle,
  members,
  onEdit,
  onSource,
  extraActions,
}: {
  group: ExpenseGroupRowDto;
  open: boolean;
  onToggle: () => void;
  members: readonly MemberRef[];
  onEdit?: ((row: ExpenseRowDto) => void) | undefined;
  onSource?: ((sourceId: string, label: string) => void) | undefined;
  extraActions?: ((row: ExpenseRowDto) => ActionMenuItem[]) | undefined;
}) {
  const t = useT();
  const label = t("exp.list.groupCosts", { count: group.childCount });

  return (
    <>
      <Table.Row>
        <Table.Cell>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="rounded-control text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <span className="font-medium">{group.label}</span>
            {/*
              The chevron is decorative: `aria-expanded` on the button already says the state, and
              naming the arrow would say the control's purpose twice, once vaguely. 019's rule.

              HEROICONS RATHER THAN A UNICODE GLYPH. It was `\u25be` / `\u25b8` — BLACK DOWN-POINTING
              and BLACK RIGHT-POINTING SMALL TRIANGLE — which are TEXT: they take their shape from
              whichever font resolves them, so they render solid and heavy on one platform and
              hair-thin on another, sit on the text baseline rather than centred against the label,
              and cannot be given a stroke weight. Beside the drawn chevrons `FilterSelect` and
              `SelectField` already use, they were the odd one out on the page.

              `@heroicons/react` is an OPTIONAL PEER of this package, which identity already
              establishes for `HouseholdSwitcher`. It is not pulled into `@gladis/ui` — that package
              depends on no icon set and draws its two paths by hand, and one consumer wanting an
              icon is not a reason to change that.
            */}
            {open ? (
              <ChevronDownIcon aria-hidden="true" className="ml-2 inline size-4 text-muted" />
            ) : (
              <ChevronRightIcon aria-hidden="true" className="ml-2 inline size-4 text-muted" />
            )}
            {/*
              The child count sits where a category sits on an ordinary row — same slot, same weight.
              Better than "Mixed", which a vehicle's costs would say almost every time.
            */}
            <div className="text-xs text-muted">{label}</div>
          </button>
        </Table.Cell>
        {/* Both hold SETS rather than values. See the header comment. */}
        <Table.Cell>
          <span className="text-muted">&mdash;</span>
        </Table.Cell>
        <Table.Cell>
          <span className="text-muted">&mdash;</span>
        </Table.Cell>
        <Table.Cell>{intervalLabel(group.interval)}</Table.Cell>
        <NextDueCell row={group} />
        <Table.Cell numeric>{formatEur(group.amountInEuros)}</Table.Cell>
        <Table.Cell numeric>
          <div className="flex items-center justify-end gap-3">
            {onSource === undefined ? null : (
              <button
                type="button"
                onClick={() => {
                  onSource(group.sourceId, group.label);
                }}
                className="rounded-control px-1 text-sm text-muted underline decoration-dotted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {t("exp.list.groupOpen")}
              </button>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
      {open
        ? group.children.map((child) => (
            <LeafRow
              key={child.id}
              row={child}
              members={members}
              onEdit={onEdit}
              extraActions={extraActions}
              nested
            />
          ))
        : null}
    </>
  );
}

/**
 * The recurring-expenses table. **One component, rendered on two pages.**
 *
 * ==========================================================================================
 * IT IS EXPORTED BECAUSE THE VEHICLE DETAIL PAGE SHOWS THE SAME ROWS
 * ==========================================================================================
 *
 * A vehicle's recurring costs ARE recurring expenses, so the table that lists them is this table:
 * the same six columns in the same order, the same category under the name, the same card and
 * "whose" cells, the same row actions. That page had a five-column table of its own for a day and it
 * was already different in every one of those respects — it showed a category column instead of a
 * card, no "whose" at all, and offered only "detach" where this one offers edit and archive.
 *
 * The extraction is deliberately a MOVE and not a rewrite: `ExpenseList` renders this and nothing
 * else, so there is no version of the table that only one page gets.
 *
 * ==========================================================================================
 * WHAT THE SECOND CALLER OMITS, AND WHY THAT IS SAFE
 * ==========================================================================================
 *
 * `sortFor` is optional — a three-row table has nothing to sort and a header that offered it would
 * be a control that changes nothing. `onSource` is optional because a group cannot appear inside a
 * vehicle's own costs: it IS the group. `extraActions` is how "Detach from this vehicle" reaches
 * the row menu without this file learning what a vehicle is.
 */
export interface ExpenseTableProps {
  rows: readonly ExpenseListRow[];
  members: readonly MemberRef[];
  onEdit?: ((row: ExpenseRowDto) => void) | undefined;
  onAttribute?: ((row: ExpenseRowDto) => void) | undefined;
  onSource?: ((sourceId: string, label: string) => void) | undefined;
  /** Per-column sort state. Omitted where the table does not sort. */
  sortFor?: ((column: ExpenseSortColumn) => TableSortState) | undefined;
  /** Which groups are open, and how to toggle one. Both absent where there are no groups. */
  openGroups?: ReadonlySet<string> | undefined;
  onToggleGroup?: ((sourceId: string) => void) | undefined;
  /** Appended to every row's action menu. */
  extraActions?: ((row: ExpenseRowDto) => ActionMenuItem[]) | undefined;
  /**
   * Each row's next due date (feature 024). Absent: no "Next due" column at all — the vehicle detail page,
   * whose costs are a three-row table and whose dates are on the Expenses page beside the rest.
   */
  nextDue?: Readonly<Record<string, string | null>> | undefined;
  className?: string | undefined;
}

/** The shape `Table.HeaderCell`'s `sort` prop asks for. */
export interface TableSortState {
  direction: "asc" | "desc" | null;
  onToggle: () => void;
}

export function ExpenseTable({
  rows,
  members,
  onEdit,
  onAttribute,
  onSource,
  sortFor,
  openGroups,
  onToggleGroup,
  extraActions,
  nextDue,
  className = "mt-5",
}: ExpenseTableProps): ReactNode {
  const t = useT();

  return (
    <NextDueContext.Provider value={nextDue ?? null}>
      <Table className={className}>
        {/*
        THE COMPOUND COMPONENTS, not raw `<th>`/`<td>`.
        
        This table was written with bare table elements and its own ad-hoc classes, and it was the only
        one in the app that was — savings and properties both use these. The primitive carries the cell
        padding, the row rules, `tabular-nums` on numeric cells and, crucially, the SAME right-alignment
        on a numeric header as on its column, from one `numeric` flag. Set by hand on the cell and
        forgotten on the header, a money column's figures sit right and its title sits left, which is
        exactly the "columns and values do not line up" this table had.
      */}
        <Table.Head>
          {/*
          SIX COLUMNS, AND THE ORDER IS THE SENTENCE THE PAGE IS TRYING TO SAY.

          WHAT it is (with its category under the name, because a category is a property of the
          expense and not a fact of equal weight to it) — then WHERE the money came from and WHOSE
          cost it was, adjacent because they are the two halves of the settle-up: a row paid from one
          member's card and held entirely by the other IS the transfer, visible on the row rather than
          only in the panel above — then HOW OFTEN and HOW MUCH, together at the right, because
          "€100.00" is not a figure anybody can read without the interval beside it.

          THE MONTHLY EQUIVALENT COLUMN IS GONE, and that costs something worth naming. It was the
          normalised figure — €100.00 weekly shown as €433.33 a month — and it is what made every
          settle-up line reproducible by hand from the numbers printed on the row (FR-064). The
          interval beside the amount is what the reader now has instead, and for a MONTHLY expense the
          two figures were always the same number twice. It is the non-monthly rows that lose it.
        */}
          {/*
          THREE OF THE SIX SORT, AND THE OTHER THREE ARE NOT OVERSIGHTS.

          "Paid from" and "Whose" hold sets rather than values — a row can name two members at
          60/40, and there is no order on that which a reader could predict or check. Sorting them
          would have to invent a rule (the first split? the largest? the alphabetically first
          name?) and then be wrong for whichever one the reader had in mind. The actions column
          holds no data at all.
        */}
          <Table.Row>
            <Table.HeaderCell {...(sortFor ? { sort: sortFor("description") } : {})}>
              {t("exp.form.descriptionLabel")}
            </Table.HeaderCell>
            <Table.HeaderCell>{t("exp.list.colPaidFrom")}</Table.HeaderCell>
            <Table.HeaderCell>{t("exp.list.colWhose")}</Table.HeaderCell>
            <Table.HeaderCell {...(sortFor ? { sort: sortFor("interval") } : {})}>
              {t("exp.list.colInterval")}
            </Table.HeaderCell>
            {nextDue === undefined ? null : (
              <Table.HeaderCell {...(sortFor ? { sort: sortFor("nextDue") } : {})}>
                {t("exp.list.colNextDue")}
              </Table.HeaderCell>
            )}
            <Table.HeaderCell numeric {...(sortFor ? { sort: sortFor("amount") } : {})}>
              {t("exp.form.amount")}
            </Table.HeaderCell>
            <Table.HeaderCell numeric>
              <span className="sr-only">{t("exp.list.actions")}</span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {rows.map((row) =>
            isGroupRow(row) ? (
              <GroupRows
                key={row.sourceId}
                group={row}
                open={openGroups?.has(row.sourceId) ?? false}
                onToggle={() => {
                  onToggleGroup?.(row.sourceId);
                }}
                members={members}
                onEdit={onEdit}
                onSource={onSource}
                extraActions={extraActions}
              />
            ) : (
              <LeafRow
                key={row.id}
                row={row}
                members={members}
                onEdit={onEdit}
                onAttribute={onAttribute}
                extraActions={extraActions}
              />
            ),
          )}
        </Table.Body>
      </Table>
    </NextDueContext.Provider>
  );
}
